import type { I18nKey } from '../i18n/keys';

/**
 * Pure helpers for a Process schedule: the five-field cron string is the stored truth; the form
 * (repeat / day / time) and the words in the table are both derived from it. No timezone maths
 * here (that needs cron-parser and lives in the api and the worker); these functions only look
 * at the shape of the expression.
 */

export type RepeatKind = 'daily' | 'weekly' | 'monthly' | 'custom';

export interface ScheduleForm {
  repeat: RepeatKind;
  /** weekly: 1 (Monday) … 7 (Sunday); monthly: 1 … 31 */
  day?: number;
  /** "HH:MM" */
  time?: string;
  /** custom: the raw expression */
  cron?: string;
}

export interface ScheduleDescription {
  key: I18nKey;
  params: Record<string, string>;
}

const FIELD = /^[0-9A-Za-z*,/\-?#LW]+$/;
const INT = /^\d{1,2}$/;

/** Shape check only: five whitespace-separated fields of cron characters. Real parsing is cron-parser's job. */
export function isFiveFieldCron(cron: string): boolean {
  const fields = cron.trim().split(/\s+/);
  return fields.length === 5 && fields.every((f) => FIELD.test(f));
}

const two = (n: number) => String(n).padStart(2, '0');

function simpleTime(minute: string, hour: string): string | null {
  if (!INT.test(minute) || !INT.test(hour)) return null;
  const m = Number(minute);
  const h = Number(hour);
  if (m > 59 || h > 23) return null;
  return `${two(h)}:${two(m)}`;
}

/** Cron day-of-week 0–7 (0 and 7 are Sunday) → 1 (Monday) … 7 (Sunday). */
function isoWeekday(dow: number): number {
  return dow === 0 ? 7 : dow;
}

/** The form that produces this cron, or `custom` when the expression does not fit daily / weekly / monthly. */
export function formFromCron(cron: string | null | undefined): ScheduleForm {
  if (!cron) return { repeat: 'daily', time: '09:00' };
  const fields = cron.trim().split(/\s+/);
  if (fields.length === 5) {
    const [minute, hour, dom, month, dow] = fields as [string, string, string, string, string];
    const time = simpleTime(minute, hour);
    if (time && month === '*') {
      if (dom === '*' && dow === '*') return { repeat: 'daily', time };
      if (dom === '*' && INT.test(dow) && Number(dow) <= 7) return { repeat: 'weekly', day: isoWeekday(Number(dow)), time };
      if (dow === '*' && INT.test(dom) && Number(dom) >= 1 && Number(dom) <= 31) return { repeat: 'monthly', day: Number(dom), time };
    }
  }
  return { repeat: 'custom', cron: cron.trim() };
}

/** The cron for a form; null when the form is incomplete or the custom expression has the wrong shape. */
export function cronFromForm(form: ScheduleForm): string | null {
  if (form.repeat === 'custom') return form.cron && isFiveFieldCron(form.cron) ? form.cron.trim().split(/\s+/).join(' ') : null;
  const m = /^(\d{2}):(\d{2})$/.exec(form.time ?? '');
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return null;
  if (form.repeat === 'daily') return `${minute} ${hour} * * *`;
  const day = form.day;
  if (day === undefined || !Number.isInteger(day)) return null;
  if (form.repeat === 'weekly') return day >= 1 && day <= 7 ? `${minute} ${hour} * * ${day === 7 ? 0 : day}` : null;
  return day >= 1 && day <= 31 ? `${minute} ${hour} ${day} * *` : null;
}

/**
 * Words for the table: "щодня о 09:00", "щопонеділка о 09:00", "1-го числа о 10:00", otherwise the raw
 * expression. Returns an i18n key plus params so the strings live in the dictionary; `tz` is passed
 * through for callers that want to show it next to the words.
 */
export function describeSchedule(cron: string | null | undefined, tz: string): ScheduleDescription {
  if (!cron) return { key: 'processes.schedule.none', params: { tz } };
  const form = formFromCron(cron);
  const time = form.time ?? '';
  switch (form.repeat) {
    case 'daily':
      return { key: 'processes.schedule.daily', params: { time, tz } };
    case 'weekly':
      return { key: `processes.schedule.weekly.${form.day as 1 | 2 | 3 | 4 | 5 | 6 | 7}`, params: { time, tz } };
    case 'monthly':
      return { key: 'processes.schedule.monthly', params: { day: String(form.day), time, tz } };
    default:
      return { key: 'processes.schedule.cron', params: { cron: form.cron ?? cron, tz } };
  }
}
